import { assert } from '@sama/js-core';
import { get, matches } from 'lodash';
import { Color } from 'three';
import { ShapeState3D } from '../../../cross-team-shared/annotation/shared/state/shape-state';
import { defaultAnnotationColor } from '../../../cross-team-shared/constants';
import {
  getDynamicTag,
  isDynamicOutput,
} from '../../../cross-team-shared/misc-services/tag-helper';
import { AnswerOption } from '../../../team-apis/otterdam/answer-option';
import { AbstractOutputWithAnswerOptions } from '../../../team-apis/otterdam/outputs/output';
import { PointCloudOutput } from '../../../team-apis/otterdam/outputs/point-cloud-output';
import { Project } from '../../../team-apis/otterdam/project';
import { PointCloudContentState } from '../../state/point-cloud-content-state';
import { defaultPolyShapeColor } from './poly-shape/abstract-polyshape-helper';

export const highlightColor = '#ffff00';

export enum MeasurementColors {
  Standard = '#90E5C6',
  HighLight = '#FFFA0C',
}

export function isSameHexColor(color: Color, hex: number) {
  return color.getHexString() === hex.toString(16);
}

export function getShapeColor(
  shape: ShapeState3D,
  state: PointCloudContentState,
) {
  if (!state.project.use_annotation_store) {
    const { output, project, render } = state;
    return getShapeColorObjectless(shape, output, project, render.frameNumber);
  }

  const annotationTransientState = state.render.shapeTransientInfo[shape.index];
  assert(
    annotationTransientState,
    'transient state must be defined as long as the shape is not in progress',
  );

  return annotationTransientState.color;
}

export function getShapeColorIfHighlighted(
  shape: ShapeState3D,
  state: PointCloudContentState,
) {
  const color = getShapeColor(shape, state);
  const { selection, highlightedShapeIndex } = state.render;
  const shapeIndex = shape.index;
  const isHighlighted =
    shapeIndex in selection || shapeIndex === highlightedShapeIndex;
  return isHighlighted ? highlightColor : color;
}

/** @deprecated used for old projects without objects */
export function getShapeColors(output: PointCloudOutput, project: Project) {
  const colorOutput = getColorOutput(output, project);

  let shapeColors: string[];
  if (colorOutput) {
    shapeColors = colorOutput.answer_options
      .map((answerOption) => answerOption.color)
      .filter(isNotNull);
  } else {
    shapeColors = [];
  }

  return shapeColors;
}

/** @deprecated used for old projects without objects */
export function getDefaultColor(shape: ShapeState3D) {
  if (shape.type === 'line' || shape.type === 'polygon') {
    return defaultPolyShapeColor;
  }
  return defaultAnnotationColor;
}

function isNotNull<T>(value: T | null): value is T {
  return !!value;
}

/** @deprecated used for old projects without objects */
function getShapeColorObjectless(
  shape: ShapeState3D,
  output: PointCloudOutput,
  project: Project,
  frameNumber: number,
) {
  if (shape.transparent) {
    return getDefaultColor(shape);
  }

  const colorOutput = getColorOutput(output, project);
  if (!colorOutput) {
    return getDefaultColor(shape);
  }

  const tag = getColorOutputTag(
    colorOutput,
    shape,
    frameNumber,
    output.answer_options,
  );
  const colorOptions = colorOutput.answer_options;
  const color = get(colorOptions.find(matches({ name: tag })), ['color']);
  return color ?? getDefaultColor(shape);
}

/** @deprecated used for old projects without objects */
function getColorOutputTag(
  colorOutput: AbstractOutputWithAnswerOptions,
  shape: ShapeState3D,
  frameNumber: number,
  answerOptions: AnswerOption[],
) {
  if (isDynamicOutput(colorOutput.name, answerOptions)) {
    return getDynamicTag(shape.dynamic_tags[colorOutput.name], frameNumber);
  } else {
    return shape.tags[colorOutput.name];
  }
}

/** @deprecated used for old projects without objects */
function getColorOutput(output: PointCloudOutput, project: Project) {
  if (!output.meta.nested_output_color_id) {
    return undefined;
  }
  return project.outputs.find(
    matches({ id: output.meta.nested_output_color_id }),
  ) as AbstractOutputWithAnswerOptions;
}
